import {
  Component, Input, OnInit, OnChanges,
  SimpleChanges, ChangeDetectorRef
} from '@angular/core';
import {
  NextBestActionService,
  NextBestActionResult,
  NbaFeedback
} from 'src/app/services/next-best-action.service';

@Component({
  selector: 'calsoft-next-best-action-card',
  templateUrl: './next-best-action-card.component.html',
  styleUrls: ['./next-best-action-card.component.scss']
})
export class NextBestActionCardComponent implements OnInit, OnChanges {

  @Input() leadId: number;

  recommendation: NextBestActionResult | null = null;
  isLoading = false;
  isRefreshing = false;
  errorMsg: string | null = null;
  feedbackSubmitted = false;

  /** Map action type → Material icon name */
  readonly actionIcons: Record<string, string> = {
    'Schedule Sales Call': 'phone',
    'Send Personalized Follow-Up Email': 'email',
    'Add To Nurture Campaign': 'spa',
    'Escalate To Senior Sales Rep': 'supervisor_account',
    'Send Proposal': 'description',
    'Request Product Demo': 'slideshow',
    'Offer Discount': 'local_offer',
    'Re-Engagement Campaign': 'cached',
    'WhatsApp Outreach': 'chat',
    'Executive Follow-Up': 'contact_mail',
    'Close Lost': 'cancel',
    'Wait For Engagement': 'hourglass_empty',
    'Qualification Call': 'record_voice_over',

    // Backwards compatibility legacy keys
    CALL: 'phone',
    EMAIL_FOLLOWUP: 'email',
    MEETING: 'event',
    NURTURE: 'spa',
    ESCALATE: 'supervisor_account'
  };

  /** Map action type → colour class */
  readonly actionColors: Record<string, string> = {
    'Schedule Sales Call': 'nba-call',
    'Send Personalized Follow-Up Email': 'nba-email',
    'Add To Nurture Campaign': 'nba-nurture',
    'Escalate To Senior Sales Rep': 'nba-escalate',
    'Send Proposal': 'nba-proposal',
    'Request Product Demo': 'nba-demo',
    'Offer Discount': 'nba-discount',
    'Re-Engagement Campaign': 'nba-reengagement',
    'WhatsApp Outreach': 'nba-whatsapp',
    'Executive Follow-Up': 'nba-executive',
    'Close Lost': 'nba-closelost',
    'Wait For Engagement': 'nba-wait',
    'Qualification Call': 'nba-qualification',

    // Backwards compatibility legacy keys
    CALL: 'nba-call',
    EMAIL_FOLLOWUP: 'nba-email',
    MEETING: 'nba-meeting',
    NURTURE: 'nba-nurture',
    ESCALATE: 'nba-escalate'
  };

  constructor(
    private nbaService: NextBestActionService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadLatest();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['leadId']) {
      const cur = changes['leadId'].currentValue;
      if (cur) {
        this.recommendation = null;
        this.feedbackSubmitted = false;
        this.loadLatest();
      }
    }
  }

  // ── load latest cached recommendation ─────────────────────────────────────

  loadLatest(): void {
    if (!this.leadId) return;
    this.isLoading = true;
    this.errorMsg = null;
    this.nbaService.getLatest(this.leadId).subscribe({
      next: result => {
        this.recommendation = result;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  // ── generate fresh recommendation (calls LLM) ─────────────────────────────

  refresh(): void {
    if (!this.leadId || this.isRefreshing) return;
    this.isRefreshing = true;
    this.errorMsg = null;
    this.feedbackSubmitted = false;

    this.nbaService.generate(this.leadId).subscribe({
      next: result => {
        this.recommendation = result;
        this.isRefreshing = false;
        this.cdr.markForCheck();
      },
      error: err => {
        this.errorMsg = 'Could not generate recommendation. Please try again.';
        this.isRefreshing = false;
        this.cdr.markForCheck();
      }
    });
  }

  // ── feedback ──────────────────────────────────────────────────────────────

  submitFeedback(feedback: NbaFeedback): void {
    if (!this.recommendation?.recommendationId || this.feedbackSubmitted) return;
    this.nbaService.submitFeedback(this.recommendation.recommendationId, feedback).subscribe({
      next: () => {
        this.feedbackSubmitted = true;
        this.cdr.markForCheck();
      }
    });
  }

  // ── helpers ───────────────────────────────────────────────────────────────

  get actionIcon(): string {
    if (!this.recommendation) return 'lightbulb';
    const action = this.recommendation.recommendedAction || this.recommendation.actionType;
    return this.actionIcons[action] || 'lightbulb';
  }

  get actionColorClass(): string {
    if (!this.recommendation) return '';
    const action = this.recommendation.recommendedAction || this.recommendation.actionType;
    return this.actionColors[action] || '';
  }

  get confidenceLabel(): string {
    if (!this.recommendation) return '';
    const c = this.recommendation.confidence;
    if (typeof c === 'string') {
      if (c === 'HIGH') return 'High';
      if (c === 'MEDIUM') return 'Medium';
      if (c === 'LOW') return 'Low';
      return c;
    }
    if (c >= 80) return 'High';
    if (c >= 55) return 'Medium';
    return 'Low';
  }

  get confidenceClass(): string {
    if (!this.recommendation) return '';
    const c = this.recommendation.confidence;
    if (typeof c === 'string') {
      if (c === 'HIGH') return 'conf-high';
      if (c === 'MEDIUM') return 'conf-medium';
      if (c === 'LOW') return 'conf-low';
      return 'conf-medium';
    }
    if (c >= 80) return 'conf-high';
    if (c >= 55) return 'conf-medium';
    return 'conf-low';
  }
}
